/**
 * Browser push reminders (docs/NOTIFICATIONS.md). The browser side of Web Push:
 * - support: Push API + Notifications API + a service worker, in a secure context (MDN PushManager.subscribe). On an
 *   iPhone/iPad, Safari offers it only to a web app added to the Home Screen (iOS/iPadOS 16.4+, webkit.org/blog/13878):
 *   there we say how, instead of a button that can't work.
 * - enabling: only from a tap (browsers require a user gesture for the permission prompt and for subscribe), and only
 *   offered after a value moment (core pushAsk). `userVisibleOnly: true` and the server's VAPID public key.
 * - keeping it working: when the server's key changes (a new key pair, or a dev server's throwaway one), the old
 *   subscription can't be used; `refreshWebPush` re-subscribes quietly if permission is already granted.
 */
import type { PushSupport } from "@logwave/core";
import type { Api } from "../api.ts";

const KEY = "cc.push.vapid";
const DISMISSED = "cc.push.dismissedAt";
const base = () => ((import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/");

function store(): Storage | undefined { try { return window.localStorage; } catch { return undefined; } }

function isIos(nav: Navigator): boolean {
  return /iPad|iPhone|iPod/.test(nav.userAgent) || (nav.platform === "MacIntel" && (nav as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
}
function isStandalone(w: Window): boolean {
  return (w.matchMedia?.("(display-mode: standalone)").matches ?? false) || (w.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** What this browser can do right now. */
export function webPushSupport(w: Window = window): PushSupport {
  const nav = w.navigator;
  const has = "serviceWorker" in nav && "PushManager" in w && "Notification" in w && w.isSecureContext !== false;
  if (!has) return isIos(nav) && !isStandalone(w) ? "needs_install" : "unsupported";
  const p = (w as Window & { Notification: typeof Notification }).Notification.permission;
  return p === "granted" ? "granted" : p === "denied" ? "denied" : "default";
}

export function pushDismissedAt(): string | undefined { return store()?.getItem(DISMISSED) ?? undefined; }
export function dismissPushAsk(now = new Date()): void { try { store()?.setItem(DISMISSED, now.toISOString()); } catch { /* private mode */ } }
/** This browser has a subscription registered for the server's current key. */
export function webPushRegistered(publicKey?: string): boolean { return !!publicKey && store()?.getItem(KEY) === publicKey; }

function keyBytes(b64u: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64u.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((b64u.length + 3) % 4));
  const out = new Uint8Array(new ArrayBuffer(s.length));
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

async function subscribe(api: Api, publicKey: string): Promise<void> {
  const reg = await navigator.serviceWorker.register(`${base()}sw.js`, { scope: base() });
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (sub && store()?.getItem(KEY) !== publicKey) { await sub.unsubscribe().catch(() => undefined); sub = null; }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  const j = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!j.endpoint || !j.keys?.p256dh || !j.keys.auth) throw new Error("This browser didn't give a push address. Try again.");
  await api.registerPushDevice({ channel: "browser", subscription: { endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } }, platform: "web" });
  try { store()?.setItem(KEY, publicKey); } catch { /* fine */ }
}

/**
 * The one tap: ask the browser, subscribe, tell the server. Returns what's true after, never throws for "No".
 * Must run inside the click handler (the permission prompt needs the user's gesture).
 */
export async function enableWebPush(api: Api, publicKey: string): Promise<{ ok: boolean; support: PushSupport; message?: string }> {
  const s = webPushSupport();
  if (s === "unsupported" || s === "needs_install" || s === "denied") return { ok: false, support: s };
  const perm = s === "granted" ? "granted" : await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, support: perm === "denied" ? "denied" : "default" };
  try { await subscribe(api, publicKey); return { ok: true, support: "granted" }; }
  catch (e) { return { ok: false, support: "granted", message: e instanceof Error ? e.message : "Couldn't turn reminders on. Try again." }; }
}

/** On opening the app: permission granted but the server's key changed (or the subscription vanished) → quietly re-subscribe. */
export async function refreshWebPush(api: Api, publicKey?: string): Promise<void> {
  if (!publicKey || webPushSupport() !== "granted") return;
  const reg = await navigator.serviceWorker.getRegistration(base()).catch(() => undefined);
  const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
  if (sub && store()?.getItem(KEY) === publicKey) return;
  await subscribe(api, publicKey).catch(() => undefined);
}
